// Authentication Context
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { getUserData, hasRole } from '@3dera/core/services/auth';
import { getStoreMembershipByUserId } from '@3dera/core/services/storeMembers';
import logger from '@3dera/core/utils/logger';

const AuthContext = createContext();

// 🔒 Güvenlik Ayarları
const TOKEN_REFRESH_INTERVAL = 55 * 60 * 1000; // 55 dakika - Firebase token 60 dakikada bir yenilenir
const DEFAULT_INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 dakika varsayılan hareketsizlik süresi
const INACTIVITY_WARNING = 2 * 60 * 1000; // Çıkıştan 2 dakika önce uyarı
const HEARTBEAT_INTERVAL = 30 * 1000; // 30 saniyede bir lastSeen güncelle

// Kullanıcının seçebileceği timeout süreleri (dakika cinsinden)
// value: 0 = devre dışı
export const SESSION_TIMEOUT_OPTIONS = [
  { value: 0, label: 'Devre Dışı' },
  { value: 15, label: '15 dakika' },
  { value: 30, label: '30 dakika (Varsayılan)' },
  { value: 60, label: '1 saat' },
  { value: 120, label: '2 saat' },
];

/**
 * Sayfa kapanırken çevrimdışı durumunu güncellemeye çalışır.
 * sendBeacon auth header desteklemediği için token olmadan gönderilir.
 * Başarısız olursa heartbeat timeout (2 dk) devreye girer ve
 * kullanıcıyı otomatik çevrimdışı olarak işaretler.
 */
const sendOfflineBeacon = (uid) => {
  try {
    if (!uid) return;
    // updateDoc pagehide'da güvenilmez ama yine de deneyelim
    // Başarısız olursa heartbeat timeout (admin panelde 2 dk) devreye girer
    const userRef = doc(db, 'users', uid);
    updateDoc(userRef, { isOnline: false, tabActive: false, lastSeen: serverTimestamp() }).catch(() => {});
  } catch (e) {
    // Sessizce başarısız ol
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Aktif mağaza üyeliği (owner DEĞİL; manager/staff). Üye ise mağaza paneline erişebilir.
  const [storeMembership, setStoreMembership] = useState(null);

  const tokenRefreshTimerRef = useRef(null);
  const inactivityTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // 🔒 Token yenileme fonksiyonu
  const refreshAuthToken = useCallback(async () => {
    if (currentUser) {
      try {
        await currentUser.getIdToken(true);
        logger.log('Auth token yenilendi');
      } catch (error) {
        logger.error('Token yenileme hatası:', error);
        await signOut(auth);
        window.dispatchEvent(new CustomEvent('app-toast', { detail: { message: 'Oturumunuz sona erdi. Lütfen tekrar giriş yapın.', type: 'warning' } }));
      }
    }
  }, [currentUser]);

  // 🔒 Kullanıcı bazlı timeout süresini hesapla
  // 0 = devre dışı (null döner), userData henüz yüklenmediyse de null döner (timer kurulmaz)
  const getInactivityTimeout = useCallback(() => {
    // userData henüz yüklenmediyse timer kurma — yüklendikten sonra tekrar çalışacak
    if (!userData) return null;
    const userTimeout = userData.sessionTimeout; // dakika cinsinden
    if (userTimeout === 0) return null; // Devre dışı
    if (typeof userTimeout === 'number' && userTimeout >= 15 && userTimeout <= 120) {
      return userTimeout * 60 * 1000;
    }
    return DEFAULT_INACTIVITY_TIMEOUT;
  }, [userData]);

  // 🔒 Hareketsizlik zamanlayıcısını sıfırla
  const resetInactivityTimer = useCallback(() => {
    lastActivityRef.current = Date.now();

    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }

    if (!currentUser) return;

    const timeout = getInactivityTimeout();

    // Devre dışıysa timer kurma
    if (timeout === null) return;

    // Uyarı: çıkıştan 2 dk önce
    warningTimerRef.current = setTimeout(() => {
      window.dispatchEvent(new CustomEvent('app-toast', { detail: { message: 'Hareketsizlik nedeniyle 2 dakika içinde oturumunuz kapatılacak.', type: 'warning' } }));
    }, timeout - INACTIVITY_WARNING);

    // Otomatik çıkış
    inactivityTimerRef.current = setTimeout(async () => {
      logger.log('Hareketsizlik nedeniyle oturum kapatılıyor');
      try {
        if (currentUser) {
          const userRef = doc(db, 'users', currentUser.uid);
          await updateDoc(userRef, { isOnline: false });
        }
      } catch (e) { /* ignore */ }
      await signOut(auth);
      window.dispatchEvent(new CustomEvent('app-toast', { detail: { message: 'Hareketsizlik nedeniyle oturumunuz kapatıldı.', type: 'error' } }));
    }, timeout);
  }, [currentUser, getInactivityTimeout]);

  // Firebase auth state değişikliklerini dinle
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      try {
        if (user) {
          // Kullanıcı giriş yaptıysa Firestore'dan ek bilgilerini al
          const result = await getUserData(user.uid);
          if (result.success) {
            const data = result.data;
            // Hesap kullanıcı tarafından devre dışı bırakıldıysa girişe izin verme
            if (data.accountStatus === 'deactivated_by_user') {
              try {
                sessionStorage.setItem('account_deactivated_message', '1');
              } catch (e) {}
              await signOut(auth);
              setUserData(null);
              setCurrentUser(null);
              return;
            }
            setUserData(data);

            // Eski kullanıcılarda eksik KVKK/Kullanım Koşulları alanlarını standartlaştır
            if (data.termsAcceptedAt === undefined || data.kvkkAcceptedAt === undefined) {
              try {
                const patchFields = {};
                if (data.termsAcceptedAt === undefined) patchFields.termsAcceptedAt = null;
                if (data.kvkkAcceptedAt === undefined) patchFields.kvkkAcceptedAt = null;
                const userRef = doc(db, 'users', user.uid);
                await updateDoc(userRef, patchFields);
              } catch (e) {
                // Sessizce devam et - Layout overlay yine de çalışacak
              }
            }
          } else {
            // Eğer Firestore'dan veri alınamazsa, temel kullanıcı bilgilerini oluştur
            setUserData({
              firstName: user.displayName?.split(' ')[0] || user.email?.split('@')[0] || 'Kullanıcı',
              lastName: user.displayName?.split(' ').slice(1).join(' ') || '',
              roles: ['user'],
              email: user.email
            });
          }

          // Son giriş ve çevrimiçi durumu. isOnline = sekme açık (arka planda da), tabActive = sekme odakta.
          try {
            const userRef = doc(db, 'users', user.uid);
            await setDoc(userRef, {
              lastLogin: serverTimestamp(),
              lastSeen: serverTimestamp(),
              isOnline: true,
              tabActive: typeof document !== 'undefined' ? document.visibilityState === 'visible' : true
            }, { merge: true });
          } catch (error) {
            logger.error('❌ Son giriş zamanı güncellenemedi:', error);
          }

          // Token yenileme timer'ını başlat
          tokenRefreshTimerRef.current = setInterval(refreshAuthToken, TOKEN_REFRESH_INTERVAL);
        } else {
          // Çıkış yapıldığında isOnline güncellemesi logoutUser içinde (oturum açıkken) yapıldı.
          // Burada tekrar denemiyoruz; signOut sonrası auth null olduğu için Firestore yazması kurallara takılabilir.
          setUserData(null);

          if (tokenRefreshTimerRef.current) clearInterval(tokenRefreshTimerRef.current);
        }
      } catch (error) {
        logger.error('❌ Auth state güncellenirken hata:', error);
      } finally {
        setLoading(false);
      }
    });

    // Cleanup function
    return () => {
      unsubscribe();
      if (tokenRefreshTimerRef.current) clearInterval(tokenRefreshTimerRef.current);
    };
  }, [refreshAuthToken]);

  // 🔒 Sayfa görünürlük değişikliklerini dinle (sekme değiştirme)
  useEffect(() => {
    if (!currentUser) return;

    const handleVisibilityChange = async () => {
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        if (document.visibilityState === 'visible') {
          // Sekme odakta: çevrimiçi + aktif
          await setDoc(userRef, { lastSeen: serverTimestamp(), isOnline: true, tabActive: true }, { merge: true });
        } else {
          // Sekme arka planda: çevrimiçi KAL, sadece "aktif" işaretini kaldır (kapanışta beacon offline yapar)
          await setDoc(userRef, { lastSeen: serverTimestamp(), isOnline: true, tabActive: false }, { merge: true });
        }
      } catch (e) {
        logger.error('❌ Durum güncellenemedi:', e);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentUser]);

  // Çevrimiçi kalp atışı: sekme görünürken periyodik lastSeen güncelle (çökme/kesinti sonrası lastSeen ile offline sayılabilir)
  useEffect(() => {
    if (!currentUser) return;

    // Heartbeat: sekme arka planda olsa da çevrimiçi kal (kapanınca beacon offline yapar).
    // (Tarayıcılar arka plan zamanlayıcılarını ~60sn'ye kısar; çevrimiçi eşiği bunu tolere eder.)
    const tick = async () => {
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        await setDoc(userRef, {
          lastSeen: serverTimestamp(),
          isOnline: true,
          tabActive: document.visibilityState === 'visible'
        }, { merge: true });
      } catch (e) {
        logger.error('❌ Heartbeat güncellenemedi:', e);
      }
    };

    const id = setInterval(tick, HEARTBEAT_INTERVAL);
    return () => clearInterval(id);
  }, [currentUser]);

  // Sekme/kapatma: sayfa kapanırken çevrimdışı yap
  // sendBeacon kullanarak güvenilir offline sinyali gönder (async updateDoc kapanmada çalışmaz)
  useEffect(() => {
    if (!currentUser) return;

    const handlePageHide = () => {
      sendOfflineBeacon(currentUser.uid);
    };

    // pagehide, beforeunload'dan daha güvenilirdir (özellikle mobilde)
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('beforeunload', handlePageHide);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('beforeunload', handlePageHide);
    };
  }, [currentUser]);

  // 🔒 Hareketsizlik takibi: mouse, klavye, dokunma, kaydırma
  useEffect(() => {
    if (!currentUser) return;

    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    let lastReset = 0;
    const onActivity = () => {
      const now = Date.now();
      // Her 10 saniyede en fazla bir kez timer sıfırla (performans)
      if (now - lastReset < 10000) return;
      lastReset = now;
      resetInactivityTimer();
    };

    activityEvents.forEach(event => {
      document.addEventListener(event, onActivity, { passive: true });
    });

    // İlk timer'ı başlat
    resetInactivityTimer();

    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, onActivity);
      });
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    };
  }, [currentUser, resetInactivityTimer]);

  // Kullanıcının aktif mağaza üyeliğini yükle (manager/staff). Owner ise null döner (zaten 'seller').
  const loadStoreMembership = useCallback(async (uid) => {
    if (!uid) { setStoreMembership(null); return; }
    try {
      const res = await getStoreMembershipByUserId(uid);
      setStoreMembership(res?.data || null);
    } catch (error) {
      setStoreMembership(null);
    }
  }, []);

  useEffect(() => {
    if (currentUser) {
      loadStoreMembership(currentUser.uid);
    } else {
      setStoreMembership(null);
    }
  }, [currentUser, loadStoreMembership]);

  const refreshUserData = useCallback(async () => {
    if (!currentUser) return;
    try {
      const result = await getUserData(currentUser.uid);
      if (result.success) {
        setUserData(result.data);
      }
      await loadStoreMembership(currentUser.uid);
    } catch (error) {
      logger.error('❌ refreshUserData hatası:', error);
    }
  }, [currentUser, loadStoreMembership]);

  // D10: owner önce — seller rolündeyse kendi uid'i mağaza id'si; değilse üyelik storeId'si
  const isSellerRole = Array.isArray(userData?.roles) && userData.roles.includes('seller');
  const myStoreId = (isSellerRole ? currentUser?.uid : null) || storeMembership?.storeId || null;
  const myStoreRole = isSellerRole ? 'owner' : (storeMembership?.role || null);

  const value = {
    currentUser,
    userData,
    storeMembership,
    loading,
    isAuthenticated: !!currentUser,
    refreshUserData,
    myStoreId,
    myStoreRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;

